import { html, LitElement } from 'lit';
import { Meter } from '@common/mobx-stores/Meter.js';
import { LifeCycleExecuteReason } from '@services/adapters/app-lifecycle/index.js';
import { MeterCentral } from '@services/datacollection/MeterCentral.js';
import { setUpAnalytics } from '@utils/analyticsHelpers.js';
import { EventBinder } from '@utils/EventBinder.js';
import { keyboardEvents } from '@utils/keyboardEvents.js';
import { getText } from '@utils/i18n.js';
import {
  cookieConsentManager,
  CookieConsentManager,
} from '@services/cookieconsentmanager/CookieConsentManager.js';
import { isFeatureFlagEnabled } from '@services/featureflags/featureFlags.js';

/**
 * @import {
 *   IProject,
 *   IAppLifecycle,
 *   IExperiment,
 *   IDataWorld,
 *   IDeviceManager,
 *   ISensorWorld,
 *   IDataCollection,
 *   IAppManifest,
 *   IEventBinder,
 * } from '@services:next/types'
 *
 * @import {DelayType} from '@services/dataworld/DataWorld.js'
 */

export const onDeviceReady = () => {
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    window.StatusBar?.backgroundColorByHexString('#27292a');
  }

  if (
    window &&
    window.device.platform.toLowerCase() === 'android' &&
    Number(window.device.version) >= 15
  ) {
    const app = `${APP_ID.toLowerCase()}-app`;
    const appEl = document.querySelector(app);

    const topInset = getComputedStyle(appEl).getPropertyValue('--safe-area-inset-top');

    if (parseInt(topInset) === 0) {
      appEl.style.setProperty('--safe-area-inset-bottom', '1.5rem');
    } else {
      appEl.style.setProperty('--safe-area-inset-bottom', '3rem');
    }

    window.screen.orientation.addEventListener('change', event => {
      const { type } = event.target;
      if (parseInt(topInset) !== 0) {
        if (type.includes('landscape')) {
          appEl.style.setProperty('--safe-area-inset-bottom', '2rem');
        } else {
          /* portrait */
          appEl.style.setProperty('--safe-area-inset-top', 'env(safe-area-inset-top, 0)');
          appEl.style.setProperty('--safe-area-inset-bottom', '3rem');
        }
      }
    });
  }
};

if (typeof window !== 'undefined') {
  window.Smart = { ...window.Smart, License: '9B3C72B9-D78F-5E17-8D07-0CBC0E1EDC29' };
}

/**
 * @typedef {Object} VseAppInfo
 * @property {string} appName app's full name, e.g. "Vernier XYZ Analysis"
 * @property {string} fileExtension app's document file extension, including
 * the dot.
 * @property {string} [sampleExperimentAddress] optional address where sample
 * experiments are located (used for some lookups)
 */

/**
 * VseApp is a baseclass for VSE application instances. It might eventually
 * contain much of the boilerplate code and properties duplicated in GaApp,
 * IaApp, SaApp, and VaApp.
 */
export class VseApp extends LitElement {
  static get properties() {
    return {
      // #region private state reactive properties
      // #endregion
      // #region public reactive properties
      // #endregion
    };
  }

  // static get observableProperties() {
  // 	return {
  //
  // 	}
  // }

  /**
   * Use this to bind events to various app methods.
   * @type {IEventBinder}
   */
  eventBinder = new EventBinder();

  // #endregion

  constructor() {
    super();

    this._handleQuit = this._handleQuit.bind(this);
    this._onBackButton = this._onBackButton.bind(this);
    this.trySave = this.trySave.bind(this);
  }

  // #region Lit Lifecycle Callbacks
  // eslint-disable-next-line class-methods-use-this
  async firstUpdated() {
    if (['GA', 'IA'].includes(APP_ID)) {
      document.addEventListener('deviceready', onDeviceReady, false);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('backbutton', this._onBackButton);
    document.removeEventListener('close-app', this._handleQuit);
    window.removeEventListener('beforeunload', this._handleQuit);
    this.eventBinder.unbindAll();
  }
  // #endregion

  // #region getters/setters
  /**
   * Returns specific information about this app. Subclasses may override to
   * return the information specific to the app.
   * @returns {VseAppInfo}
   */
  // eslint-disable-next-line class-methods-use-this
  get appInfo() {
    console.error('App instance must override appInfo() getter.');
    return {
      appName: '',
      fileExtension: '',
      sampleExperimentAddress: '',
    };
  }
  // #endregion

  // #region private methods
  /**
   * Called when app is about to perform a potentially lengthy operation.
   * @param {DelayType} type
   */
  _handleBeginChurn(type) {
    // Don't show wait UX unless table-next has been enabled.
    if (!isFeatureFlagEnabled('ff-table-next')) return;
    // FIXME: (jkelly) move showWait() functionality to this base class.
    const delayDescriptions = {
      createCalcColumnGroup: getText('Adding calculated column'),
      doImport: getText('Loading'),
      importVideo: getText('Loading Video'),
      setCollectionParams: getText('Setting up data collection'),
    };
    this.showWait?.(true, delayDescriptions[type] || getText('Waiting'));
  }

  _handleEndChurn() {
    this.showWait?.(false);
  }

  /**
   * Quit event handler
   * @param {Event} event emitted by quit key or quit menu or app close events.
   * @private
   */
  _handleQuit(event) {
    // For abitti, don't show the confirmation if the document is not dirty;
    // this could be generalised for all platforms.
    //
    // For abitti there is only ever a single document that is assigned to the user, so
    // only the current experiment matters (for other platforms we would need to check
    // all the keys Experiment.dirtyExperiments).
    if (PLATFORM_VARIANT === 'abitti' && !this.$services.experiment.isDirty) return;

    // PWA: Show system "leave site?" when session is not empty
    if (PLATFORM_ID === 'web' && !this.$services.dataWorld.isSessionEmpty) {
      // Calling preventDefault on beforeunload shows the dialog
      event.preventDefault();
      // Chrome [doesn't support preventDefault on beforeunload](https://bugs.chromium.org/p/chromium/issues/detail?id=866818),
      // so use the deprecated field anyway.
      event.returnValue = false;
    }

    // This kicks off the file save check and ultimately data world uninit.
    this.$services.appLifecycle.closeWindow(event);
  }

  /**
   * Called when user presses the back button (on PWA et al.)
   * @returns {Promise} Promise that resolves after the last popover is closed
   * or after attempting to close the main window (this latter case will ask to
   * save file if necessary).
   * @private
   */
  _onBackButton() {
    const { popoverManager } = this.$services;
    return popoverManager.hasPopovers()
      ? this.$services.appLifecycle.closeWindow()
      : this.$services.popoverManager.closeLast();
  }

  async _startSession(fileData = {}, options = {}) {
    const { dataWorld, popoverManager } = this.$services;
    if (fileData && fileData.file) {
      try {
        popoverManager.closeAll();
        const { cancelled } = await this.trySave().catch(rejection => rejection);
        if (cancelled) {
          return;
        }
        await dataWorld.importDataThrottled({ fileData, subset: options.subset });
      } catch (err) {
        console.warn(`Import failed: ${err}`);
      }
    }
  }
  // #endregion

  // #region public methods
  cookieConsentDialogTemplate() {
    return html`
      <vst-ui-user-role-selection-dialog
        .colorMode=${this.colorMode}
        .showUserRoleSelectionDialog=${cookieConsentManager.showUserRoleSelectionDialog}
        .showUserRoleSelectionSubmitButton=${cookieConsentManager.showUserRoleSelectionSubmitButton}
        @dialog-close=${cookieConsentManager.handleUserRoleDialogClose.bind(cookieConsentManager)}
        @self-id-complete=${cookieConsentManager.handleSelfIdComplete.bind(cookieConsentManager)}
        @reject-cookies=${() => {
          CookieConsentManager.acceptsAnalyticsCookies = false;
        }}
      ></vst-ui-user-role-selection-dialog>
    `;
  }

  /**
   * Initializes common properties and installs listeners and event handlers.
   * Subclasses must call `super.initApp()` before proceeding to initialize
   * themselves.
   */
  async initApp() {
    if (
      PLATFORM_VARIANT !== 'abitti' &&
      !this.$services.connectionsBlockServices &&
      ['GA', 'SA', 'VA'].includes(APP_ID)
    ) {
      const userRoleDialog = this.shadowRoot.querySelector('vst-ui-user-role-selection-dialog');
      await cookieConsentManager.getCookieConsent(userRoleDialog);

      const { $services: services } = this;
      setUpAnalytics({
        services,
        userRole: cookieConsentManager.userRole,
        gradeLevel: cookieConsentManager.gradeLevel,
        acceptsAnalyticsCookies: CookieConsentManager.acceptsAnalyticsCookies,
      });
    }

    const { appLifecycle, experiment, dataWorld, sensorWorld, nativeModules } = this.$services;

    if (!CONNECTIONS_APP_CONTEXT) {
      appLifecycle.onClose.addPromise(0, this.trySave);
      // Set up uninit
      appLifecycle.onClose.setBeforeCloseHandler(async reason => {
        // Knowing if a user chooses "stay" at the "leave site?" beforeunload
        // prompt is not possible, so skip uninit for PWA. Closing the tab should
        // be adequate.
        if (PLATFORM_ID === 'web') return;
        await this.$services.dataWorld.closeSession();
        if (reason === LifeCycleExecuteReason.CLOSE_WINDOW) await nativeModules.uninit();
        else if (reason === LifeCycleExecuteReason.RELOAD_WINDOW)
          this.$services.dataWorld.prepareForReload();
        await this.onStopDataShare?.();
      });
    }

    document.addEventListener('backbutton', this._onBackButton);
    document.addEventListener('close-app', this._handleQuit);
    if (!CONNECTIONS_APP_CONTEXT) {
      window.addEventListener('beforeunload', this._handleQuit);
    }

    this.eventBinder.bindListeners({
      source: keyboardEvents,
      target: this,
      eventMap: {
        save: 'onSaveFile',
        'save-as': 'onSaveFile',
        quit: '_handleQuit',
      },
    });

    this.eventBinder.bindListeners({
      source: dataWorld,
      target: this,
      eventMap: {
        'begin-heavy-churn': '_handleBeginChurn',
        'end-heavy-churn': '_handleEndChurn',
      },
    });

    // Create a MeterCentral instance. This object will subscribe to various
    // events and set up Meters accordingly. This is a replacement to the
    // venerable modeLayoutPrefs function which has become increasingly
    // difficult to maintain.
    this._meterCentral = new MeterCentral({ experiment, dataWorld, sensorWorld });

    if (!dataWorld.udm?.isBlock) {
      // Function that filters out specific column meters from appearing in the
      // bottom bar (e.g. live readouts bar).
      const excludeBottomBarMeters = (column, meter) => {
        let metered = meter.isVisibleInBottomBar;

        // No user created columns will be metered for the bottom BAR.
        if (column.group.isUserCreated) {
          metered = false;
        } else if (column.type === 'sensor') {
          // meter the sensor column unless it is replaced by a calculated colum
          const isCalcColumnReplaceDependent = columnId => {
            const col = dataWorld.getColumnById(columnId);
            return (
              col &&
              col.type === 'calc' &&
              col.metered &&
              !col.group.isUserCreated &&
              col.group.replaceDependent
            );
          };
          metered = !dataWorld.meters.some(
            meterItem =>
              meterItem.sensorInfo.id === meter.sensorInfo.id &&
              isCalcColumnReplaceDependent(meterItem.columnId),
          );
        } else if (column.type === 'calc') {
          // This addresses MEG-1285 which describes when auto-calc columns
          // should be metered or not on the bottom bar.
          const { group } = column;
          const hasCustomDerivative = group.customEq && group.customEq.includes('Derivative');
          const cannedDerivative =
            group.calcEquation === 'secondDerivative' || group.calcEquation === 'derivative';
          if (!hasCustomDerivative && !cannedDerivative) {
            // meter calculated column if it has a dependent sensor column
            const dependentGroups = group.calcDependentGroups.map(groupId =>
              dataWorld.getColumnGroupById(groupId),
            );
            // Don't show meters for calc-columns that are not sanctioned by
            // the column group's metered property.
            metered =
              group.metered &&
              dependentGroups.some(
                group => typeof group.sensorId === 'number' && group.sensorId !== 0,
              );
          }
        }
        return {
          bar: metered
            ? Meter.VisibilityOverride.FORCE_SHOWN
            : Meter.VisibilityOverride.FORCE_HIDDEN,
        };
      };

      this._meterCentral.addOverrideFunction(excludeBottomBarMeters);
    }
  }

  /**
   * @returns {Promise<{ cancelled?: boolean, confirmed?: boolean}>}}
   */
  async #promptCollectingConfirmNewSession() {
    /** @type {{ appManifest: IAppManifest }} */
    const { appManifest } = this.$services;

    const appName = appManifest.getAppName();

    const { promptConfirmNewSession } = await import('@utils/promptConfirmNewSession.js');
    return await promptConfirmNewSession.call(this, appName);
  }

  /**
   * Prompts user to save if necessary, and then calls the `onSaveFile()`
   * workflow.
   * @returns {Promise} Promise that resolves on successful file save. Will
   * reject if there's a file save problem OR the user cancels.
   */
  async trySave() {
    if (CONNECTIONS_APP_CONTEXT || PLATFORM_VARIANT === 'abitti') return {};

    /** @type {{ appLifecycle: IAppLifecycle, experiment: IExperiment }} */
    const { appLifecycle, experiment } = this.$services;

    const check = await experiment.checkForSave();
    let cancelSave = false;

    if (check.isCollecting) {
      const confirmation = await this.#promptCollectingConfirmNewSession();
      if (confirmation.confirmed) await experiment.stopCollection();
      cancelSave = !!confirmation.cancelled;
    }

    if (!cancelSave && check.needsSave && !this.__notifiedNewFile) {
      const { promptForSave } = await import('@utils/promptForSave.js');
      const controller = new AbortController();
      // Electron: Abort save dialog on force close so that this is resolved
      // before teardown.
      if (['macos', 'windows'].includes(PLATFORM_ID)) {
        appLifecycle.onClose.setForceCloseHandler(() => controller.abort());
      }
      const { cancelled, save } = await promptForSave.call(
        this,
        this.experimentName,
        controller.signal,
      );
      cancelSave = cancelled;
      if (save) {
        return this.onSaveFile({ throwOnCancel: true });
      }
    }

    if (cancelSave) {
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({ cancelled: true, cancelClose: true });
    }

    // don't save
    return Promise.resolve({});
  }

  /**
   * Bottleneck for saving files.
   * @param {object} [options] options for the save. What exact options are
   * expected is largely undocumented, but here are a couple of them:
   * @param {string} [options.eventName] if the eventName field is provided and
   * is `save-as` will perform force save operation.
   * @param {boolean} [options.throwOnCancel] if set, will throw an exception
   * when the user cancels in order to reset the app life cycle workflow.
   */
  async onSaveFile(options = {}) {
    const { toast, fileIO, dataWorld } = this.$services;
    const { fileExtension, sampleExperimentAddress } = this.appInfo;

    // check if we already have a filepath, and if we do, check if it has the
    // expected extension.
    const notAppFile =
      dataWorld.userFileMetaData.filepath &&
      // Android provides a file handle:
      !dataWorld.userFileMetaData.filepath.startsWith('content://') &&
      !dataWorld.userFileMetaData.filepath.endsWith(fileExtension);

    // Force save-as if we haven't saved before, or we've opened from a non-app
    // file (e.g. csv import) OR sample data experiment (indicated by the
    // specific experiment url address in the file path, currently GA only).
    const forceSaveAs =
      !dataWorld.userFileMetaData.filepath ||
      notAppFile ||
      dataWorld.userFileMetaData.filepath.includes(sampleExperimentAddress);

    if (forceSaveAs || options?.eventName === 'save-as') {
      options.saveAs = true;
      if (notAppFile) {
        const filenameParts = dataWorld.userFileMetaData.filepath.split('.');
        filenameParts.pop(); // drop the extension and let the app append the new one
        let suggestedName = filenameParts.join('.');
        if (suggestedName.includes(':')) suggestedName = suggestedName.split(':').pop();
        options.suggestedName = suggestedName;
      }
    }

    try {
      await this.showWait?.(true, getText('Saving'));
      if (await fileIO.saveFile(options)) toast.makeToast(getText('File saved'));
    } catch (err) {
      // We don't get an error back from cancel on some platforms.
      if (err) {
        console.error(err);
        toast.makeToast(getText('Save failed'), { duration: 10000 });
      }
      // Throw the magic which will reset the appLifeCycle object's promise,
      // otherwise the next quit will not invoke the file confirmation dialog.
      // eslint-disable-next-line no-throw-literal
      if (options?.throwOnCancel) throw { cancelClose: true };
    } finally {
      this.showWait?.(false);
    }

    return {};
  }

  async open(buf) {
    const { fileIO } = this.$services;
    fileIO.openFromArrayBuffer(buf);
  }

  async save() {
    const { fileIO } = this.$services;
    return fileIO.saveToArrayBuffer();
  }

  // #endregion
}
